import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import useSessionStorage from '@vimeo/core/hooks/use-storage/useClientStorage';
import { NetworkError } from '@vimeo/gctl-api/fetchers/_helpers';
import type {
  PostVideoQuestionsResult,
  PostVideoQuestionsFields,
} from '@vimeo/gctl-api/fetchers/video/questions';
import { postVideoQuestions } from '@vimeo/gctl-api/fetchers/video/questions';
import { useGctlConfig } from '@vimeo/gctl-api/hooks/GctlProvider';
import { useGetVideoQuestions } from '@vimeo/gctl-api/hooks/useVideoQuestions';
import { usePostVideoQuestionsActivation } from '@vimeo/gctl-api/hooks/useVideoQuestionsActivation';
import type { VideoQuestion } from '@vimeo/gctl-api/models/components/schemas/video-question/VideoQuestion';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { POLLING_SERVICE_KEY } from '../constants';
import * as finalCopy from '../copy/final';
import { useBokehToast } from '../hooks/useBokehToast';
import { useGetVideoSignature } from '../hooks/useGetVideoSignature';
import { useContainerDataStore } from '../stores/container-data-store';
import { handleError } from '../utils';
import { sendPlayerError } from '../utils/postMessage';

const QuestionContext = createContext<QuestionContext | null>(null);

const videoQuestionFields: PostVideoQuestionsFields = [
  'answer',
  'relevantQuotes',
  'relatedQuestions',
];

const POLLING_INTERVAL = 5000;

interface QuestionContext {
  answer: string;
  askNewQuestion: (value: string) => void;
  askPredefinedQuestion: (
    value: string,
    answer: string,
    moments?:
      | null
      | {
          timecode: number;
        }[],
    relatedQuestions?: string[],
  ) => void;
  errorData: null | Error;
  generateVideoQnA: () => void;
  isGeneratedInSession: boolean;
  isLoading: boolean;
  moments: {
    timecode: number;
    quote?: string | null;
    quoteTitle?: string;
  }[];
  predefinedQuestions: VideoQuestion[];
  question: string;
  questionResponseError: Error | null;
  questionResponseLoading: boolean;
  relatedQuestions: string[];
  resetQuestionData: () => void;
  setCanCall: (canCall: boolean) => void;
  setIsGeneratedInSession: (isGenerated: boolean) => void;
  setQuestion: (question: string) => void;
}

export const QuestionContextProvider = ({
  videoId,
  children,
  guestSignature,
}: {
  videoId: number | string;
  children: ReactNode;
  guestSignature?: string;
}) => {
  const clipId = useContainerDataStore((state) => state.videoId);
  const notify = useBokehToast();
  const { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();
  const { value: polledService } = useSessionStorage(POLLING_SERVICE_KEY, null);
  const [
    postQuestionActivation,
    { error: questionActivationPostError, loading: questionActivationPostIsLoading },
  ] = usePostVideoQuestionsActivation();
  const videoSignature = useGetVideoSignature(clipId);

  const [question, setQuestion] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canCall, setCanCall] = useState<boolean>(false);
  const [answer, setAnswer] = useState<string>('');
  const [moments, setMoments] = useState<{ timecode: number; quote?: string | null }[]>([]);
  const [predefinedQuestions, setPredefinedQuestions] = useState<VideoQuestion[]>([]);
  const [errorData, setErrorData] = useState<null | Error>(null);
  const [isGeneratedInSession, setIsGeneratedInSession] = useState<boolean>(false);
  const [relatedQuestions, setRelatedQuestions] = useState<string[]>([]);
  const [questionResponseError, setQuestionResponseError] = useState<Error | null>(null);
  const [questionResponseLoading, setQuestionResponseLoading] = useState(false);

  const isPosting = useRef(false);

  useGetVideoQuestions(
    //@ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!videoId || !canCall) {
        return null;
      }
      return {
        where: { videoId },
        select: [
          'question',
          'answer',
          'relevantQuotes',
          'relatedQuestions',
          'status',
          'languageCode',
          'languageLabel',
        ],
      };
    },
    {
      refreshInterval: isLoading ? POLLING_INTERVAL : 0,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      onSuccess: (response) => {
        if (response) {
          // Backend returns either one of two distinct response types:
          // 1. "status" - Accepted response (HTTP 202): {status: 'PRCS'|'INIT'|'NONE_EXISTING'} - still processing
          // 2. "data" - Collection response (HTTP 200): {data: [...], total, page, ...} - questions ready
          const statusResponse = response as unknown as { status?: string };
          const collectionResponse = response as unknown as { data?: typeof response };

          const isStatusResponse = !!statusResponse.status;
          const isCollectionResponse = !!collectionResponse.data;

          // Early return: no questions generated yet
          if (isStatusResponse && statusResponse.status === 'NONE_EXISTING' && isLoading) {
            return;
          }

          // Early return: empty collection
          if (
            isCollectionResponse &&
            collectionResponse.data &&
            collectionResponse.data.length === 0
          ) {
            return;
          }

          // Handle questions are ready - STATUS_DONE
          if (isCollectionResponse && collectionResponse.data) {
            setPredefinedQuestions(collectionResponse.data);
            setIsLoading(false);
            setErrorData(null);
          } else if (polledService === 'qna') {
            setIsLoading(true); // Defensive: ensure polling continues
          }
        } else {
          // Here we handle any weird edge cases where the endpoint returns something other than data.
          // This shouldn't happen but here we are.
          handleFailure();
        }
      },
      onError: (error) => {
        handleFailure(error);
      },
    },
  );

  const generateVideoQnA = () => {
    if (isLoading || !videoId) return;
    setIsGeneratedInSession(true);
    postQuestionActivation({
      // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
      where: { videoId },
    });
    setIsLoading(true);
  };

  const onAskQuestionSuccess = useCallback(
    (questionResponse: PostVideoQuestionsResult<PostVideoQuestionsFields>) => {
      setQuestionResponseLoading(false);
      setQuestionResponseError(null);
      if (questionResponse && questionResponse.answer) {
        setAnswer(questionResponse.answer);
        setMoments(questionResponse?.relevantQuotes ?? []);
        setRelatedQuestions(questionResponse.relatedQuestions ?? []);
      }
    },
    [],
  );

  const onAskQuestionFailure = useCallback(
    (error: NetworkError) => {
      setQuestionResponseError(error);
      setQuestionResponseLoading(false);
      handleError(error, finalCopy.failedToAskTheVimeoAi, notify);
      if (error?.status >= 500) {
        sendPlayerError(error);
      }
    },
    [notify],
  );

  const askQuestion = useCallback(
    async (question: string) => {
      if (!videoId) {
        return null;
      }

      const hash = getPasswordHashFromCookie(videoId);

      setQuestionResponseLoading(true);

      isPosting.current = true;

      try {
        const questionResponse = await postVideoQuestions({
          baseUrl,
          // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
          where: { videoId },
          variables: { question },
          select: videoQuestionFields,
          ...(hash
            ? { query: { password: hash, signature: guestSignature ?? videoSignature } }
            : { query: { signature: guestSignature ?? videoSignature } }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });

        onAskQuestionSuccess(questionResponse);
      } catch (error) {
        onAskQuestionFailure(error);
      } finally {
        isPosting.current = false;
      }

      return null;
    },
    [
      videoId,
      baseUrl,
      guestSignature,
      videoSignature,
      jwt,
      xVimeoPage,
      locale,
      onAskQuestionSuccess,
      onAskQuestionFailure,
    ],
  );

  const askNewQuestion = useCallback(
    (value: string) => {
      setQuestionResponseError(null);
      setQuestion(value);
      setAnswer('');
      setMoments([]);
      setRelatedQuestions([]);
      askQuestion(value);
    },
    [askQuestion],
  );

  const askPredefinedQuestion = useCallback(
    (
      value: string,
      answer: string,
      moments?: null | { timecode: number }[],
      relatedQuestions?: string[],
    ) => {
      setQuestion(value);
      setAnswer(answer);
      setMoments(moments ?? []);
      setRelatedQuestions(relatedQuestions ?? []);
    },
    [],
  );

  const resetQuestionData = useCallback(() => {
    setQuestion('');
    setAnswer('');
    setMoments([]);
    setRelatedQuestions([]);
  }, []);

  useEffect(
    function handlePostLoadingStateChange() {
      isPosting.current = questionActivationPostIsLoading;
    },
    [questionActivationPostIsLoading],
  );

  useEffect(
    function handleActivationPostError() {
      if (questionActivationPostError) {
        handleFailure(questionActivationPostError);
      }
    },
    [questionActivationPostError],
  );

  const handleFailure = useCallback(
    (error?: Error | string) => {
      setIsLoading(false);
      setErrorData(error ?? finalCopy.failedToAskTheVimeoAi);
      handleError(
        error ?? finalCopy.failedToAskTheVimeoAi,
        finalCopy.failedToAskTheVimeoAi,
        notify,
      );
    },
    [notify],
  );

  const value = {
    answer,
    askNewQuestion,
    askPredefinedQuestion,
    errorData,
    generateVideoQnA,
    isGeneratedInSession,
    isLoading,
    moments,
    predefinedQuestions,
    question,
    questionResponseError,
    questionResponseLoading,
    relatedQuestions,
    resetQuestionData,
    setCanCall,
    setIsGeneratedInSession,
    setQuestion,
  };

  return <QuestionContext.Provider value={value}>{children}</QuestionContext.Provider>;
};

export const useQuestionContext = () => {
  const context = useContext(QuestionContext);
  if (!context) {
    throw new Error('useQuestionContext must be used within a QuestionContextProvider');
  }
  return context;
};
